import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import DualListBox from "react-dual-listbox";
import "react-dual-listbox/lib/react-dual-listbox.css";
import { JobPackageService } from "./service";
import { JobService } from "../jobs/service";
import { IJob } from "../../shared/interfaces/job/IJob";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { IJobPackage } from "../../shared/interfaces/job-package/IJobPackage";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const schema = yup.object().shape({
    name: yup.string().required("validation.required"),
    jobIds: yup.array().of(yup.number()).min(1, "validation.required"),
});

const JobPackageForm = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const [jobOptions, setJobOptions] = useState<IJob[]>([]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<IJobPackage>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            jobIds: [],
        },
    });

    const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

    useEffect(() => {
        // Fetch all jobs for the DualListBox
        JobService.get({ page: 1, perPage: 1000 }).then((res) => {
            setJobOptions(res.data);
        }).catch(ErrorToast);

        if (isEdit) {
            JobPackageService.getSingle(+id!).then((res) => {
                reset({
                    name: res.name,
                    jobIds: res.jobs?.map((j: any) => j.id) || res.jobIds || [],
                });
            }).catch(ErrorToast);
        }
    }, [id, isEdit, reset]);

    const onSubmit = async (data: IJobPackage) => {
        try {
            if (isEdit) {
                await JobPackageService.update(data, +id!);
            } else {
                await JobPackageService.create(data);
            }
            SuccessToast(t(`jobPackage.${isEdit ? "successUpdate" : "successCreate"}`));
            navigate("/job-packages");
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <Row className="g-3">
                                    <Col sm={12}>
                                        <Form.Label className="required">
                                            {t("jobPackage.name")}
                                        </Form.Label>
                                        <Controller
                                            name="name"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Form.Control
                                                        type="text"
                                                        isInvalid={!!fieldState.error}
                                                        placeholder={t("jobPackage.enterName")}
                                                        {...field}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(fieldState.error?.message as string)}
                                                    </Form.Control.Feedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col sm={12}>
                                        <Form.Label className="required">
                                            {t("jobPackage.jobs")}
                                        </Form.Label>
                                        <Controller
                                            name="jobIds"
                                            control={control}
                                            render={({ field }) => (
                                                // @ts-ignore
                                                <DualListBox
                                                    options={jobOptions.map(j => ({ value: j.id, label: j.name }))}
                                                    selected={field.value || []}
                                                    onChange={(selected: any) => field.onChange(selected)}
                                                    canFilter
                                                    icons={{
                                                        moveToAvailable: <span>&lt;</span>,
                                                        moveAllToAvailable: <span>&laquo;</span>,
                                                        moveToSelected: <span>&gt;</span>,
                                                        moveAllToSelected: <span>&raquo;</span>,
                                                    }}
                                                    lang={{
                                                        availableHeader: t("job.availableJobs"),
                                                        selectedHeader: t("job.assignedJobs"),
                                                        availableFilterPlaceholder: t("general.search"),
                                                        selectedFilterPlaceholder: t("general.search"),
                                                    }}
                                                />
                                            )}
                                        />
                                        {errors.jobIds && (
                                            <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>
                                                {t(errors.jobIds.message as string)}
                                            </div>
                                        )}
                                    </Col>
                                    <Col sm={12} className="d-flex gap-2">
                                        <Button type="submit" variant="primary">
                                            {t(`jobPackage.${isEdit ? "update" : "create"}`)}
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={() => guardedNavigate("/job-packages")}>
                                            {t("button.cancel")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {unsavedChangesModal}
        </React.Fragment>
    );
};

export default JobPackageForm;
